import { onUnmounted, ref, type Ref } from 'vue'

type UseHorizontalDragScrollOptions = {
  dragThreshold?: number
}

export const useHorizontalDragScroll = (
  containerRef: Ref<HTMLElement | null>,
  options: UseHorizontalDragScrollOptions = {},
) => {
  const dragThreshold = options.dragThreshold ?? 5

  const isDragging = ref(false)
  const preventClickAfterDrag = ref(false)
  const isMouseDown = ref(false)
  const lastClientX = ref(0)
  const dragDistance = ref(0)

  const disableScrollInterference = () => {
    if (!containerRef.value) {
      return
    }

    if (containerRef.value.style.scrollSnapType !== 'none') {
      containerRef.value.style.scrollSnapType = 'none'
    }

    if (containerRef.value.style.scrollBehavior !== 'auto') {
      containerRef.value.style.scrollBehavior = 'auto'
    }
  }

  const restoreScrollInterference = () => {
    if (!containerRef.value) {
      return
    }

    containerRef.value.style.removeProperty('scroll-snap-type')
    containerRef.value.style.removeProperty('scroll-behavior')
  }

  const onMouseMove = (event: MouseEvent) => {
    if (!isMouseDown.value || !containerRef.value) {
      return
    }

    const delta = event.clientX - lastClientX.value
    lastClientX.value = event.clientX
    dragDistance.value += Math.abs(delta)

    if (dragDistance.value < dragThreshold) {
      return
    }

    isDragging.value = true
    disableScrollInterference()
    containerRef.value.scrollLeft -= delta
    event.preventDefault()
  }

  const finishDragging = () => {
    if (!containerRef.value) {
      isMouseDown.value = false
      isDragging.value = false
      return
    }

    isMouseDown.value = false
    isDragging.value = false
    restoreScrollInterference()

    if (dragDistance.value >= dragThreshold) {
      requestAnimationFrame(() => {
        if (containerRef.value) {
          containerRef.value.scrollTo({
            left: containerRef.value.scrollLeft,
            behavior: 'smooth',
          })
        }
      })

      preventClickAfterDrag.value = true
      window.setTimeout(() => {
        preventClickAfterDrag.value = false
      }, 0)
    }

    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  }

  const onMouseUp = () => {
    finishDragging()
  }

  const onMouseDown = (event: MouseEvent) => {
    if (event.button !== 0 || !containerRef.value) {
      return
    }

    event.preventDefault()

    isMouseDown.value = true
    isDragging.value = false
    dragDistance.value = 0
    lastClientX.value = event.clientX
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const shouldIgnoreClick = () => preventClickAfterDrag.value

  onUnmounted(() => {
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  })

  return {
    isDragging,
    preventClickAfterDrag,
    onMouseDown,
    shouldIgnoreClick,
  }
}
